import React, { useState, useEffect } from "react";
import api from "@/services/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Package,
  Plus,
  Minus,
  ArrowUpCircle,
  ArrowDownCircle,
  Users,
  History,
  Truck,
} from "lucide-react";

export default function AdminInventario() {
  const [productos, setProductos] = useState([]);
  const [vendedores, setVendedores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stockDialogOpen, setStockDialogOpen] = useState(false);
  const [asignarDialogOpen, setAsignarDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [movimientos, setMovimientos] = useState([]);
  const [movimientosDialogOpen, setMovimientosDialogOpen] = useState(false);

  const [stockForm, setStockForm] = useState({
    cantidad: "",
    tipo: "entrada",
    notas: "",
  });

  const [asignarForm, setAsignarForm] = useState({
    vendedor_id: "",
    producto_id: "",
    cantidad: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [productosRes, usuariosRes] = await Promise.all([
        api.get("/productos"),
        api.get("/usuarios"),
      ]);
      setProductos(productosRes.data);
      setVendedores(usuariosRes.data.filter((u) => u.rol === "vendedor" && u.activo));
    } catch (error) {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  const handleStockUpdate = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/productos/${selectedProduct.id}/stock`, {
        cantidad: parseInt(stockForm.cantidad),
        tipo: stockForm.tipo,
        notas: stockForm.notas,
      });
      toast.success(`Stock ${stockForm.tipo === "entrada" ? "aumentado" : "reducido"}`);
      setStockDialogOpen(false);
      setStockForm({ cantidad: "", tipo: "entrada", notas: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar stock");
    }
  };

  const handleAsignar = async (e) => {
    e.preventDefault();
    try {
      await api.post("/inventario/asignar", {
        vendedor_id: asignarForm.vendedor_id,
        producto_id: asignarForm.producto_id,
        cantidad: parseInt(asignarForm.cantidad),
      });
      toast.success("Inventario asignado correctamente");
      setAsignarDialogOpen(false);
      setAsignarForm({ vendedor_id: "", producto_id: "", cantidad: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al asignar");
    }
  };

  const handleVerMovimientos = async (producto) => {
    try {
      const response = await api.get(`/productos/${producto.id}/movimientos`);
      setMovimientos(response.data);
      setSelectedProduct(producto);
      setMovimientosDialogOpen(true);
    } catch (error) {
      toast.error("Error al cargar movimientos");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
    }).format(value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-torta-brown">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-inventario">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl text-torta-dark">Control de Inventario</h1>
          <p className="text-torta-muted">Gestiona el stock central y asignaciones</p>
        </div>
        <Button
          onClick={() => setAsignarDialogOpen(true)}
          className="bg-torta-brown hover:bg-torta-brown/90"
        >
          <Truck className="w-4 h-4 mr-2" />
          Asignar a Vendedor
        </Button>
      </div>

      {/* Stock Central */}
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <div className="flex items-center gap-2 mb-6">
          <Package className="w-5 h-5 text-torta-brown" />
          <h2 className="font-display text-xl text-torta-dark">Stock Central (Bodega)</h2>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {productos.map((prod) => (
            <div
              key={prod.id}
              className="border border-torta-sand rounded-xl p-5 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-display text-lg text-torta-dark">{prod.nombre}</h3>
                  <p className="text-sm text-torta-muted">{formatCurrency(prod.precio)}</p>
                </div>
                <div
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    (prod.stock_central || 0) > 20
                      ? "bg-green-100 text-green-700"
                      : (prod.stock_central || 0) > 5
                      ? "bg-amber-100 text-amber-700"
                      : "bg-red-100 text-red-700"
                  }`}
                >
                  {prod.stock_central || 0} und
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setSelectedProduct(prod);
                    setStockForm({ ...stockForm, tipo: "entrada" });
                    setStockDialogOpen(true);
                  }}
                  className="flex-1 border-green-300 text-green-600 hover:bg-green-50"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Entrada
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setSelectedProduct(prod);
                    setStockForm({ ...stockForm, tipo: "salida" });
                    setStockDialogOpen(true);
                  }}
                  className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
                >
                  <Minus className="w-4 h-4 mr-1" />
                  Salida
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleVerMovimientos(prod)}
                >
                  <History className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Stock Dialog */}
      <Dialog open={stockDialogOpen} onOpenChange={setStockDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              {stockForm.tipo === "entrada" ? (
                <ArrowUpCircle className="w-6 h-6 text-green-600" />
              ) : (
                <ArrowDownCircle className="w-6 h-6 text-red-600" />
              )}
              {stockForm.tipo === "entrada" ? "Entrada de Stock" : "Salida de Stock"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleStockUpdate} className="space-y-4 mt-4">
            <div className="bg-torta-cream rounded-xl p-4">
              <p className="text-sm text-torta-muted">Producto</p>
              <p className="font-display text-lg text-torta-dark">{selectedProduct?.nombre}</p>
              <p className="text-sm text-torta-muted">
                Stock actual: <span className="font-medium">{selectedProduct?.stock_central || 0} und</span>
              </p>
            </div>
            <div className="space-y-2">
              <Label>Cantidad</Label>
              <Input
                type="number"
                min="1"
                required
                value={stockForm.cantidad}
                onChange={(e) => setStockForm({ ...stockForm, cantidad: e.target.value })}
                placeholder="Ingrese cantidad"
              />
            </div>
            <div className="space-y-2">
              <Label>Notas (opcional)</Label>
              <Textarea
                value={stockForm.notas}
                onChange={(e) => setStockForm({ ...stockForm, notas: e.target.value })}
                placeholder="Ej: Compra a proveedor, producto dañado..."
                rows={2}
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setStockDialogOpen(false)}>
                Cancelar
              </Button>
              <Button
                type="submit"
                className={stockForm.tipo === "entrada" ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"}
              >
                Confirmar {stockForm.tipo === "entrada" ? "Entrada" : "Salida"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Asignar Dialog */}
      <Dialog open={asignarDialogOpen} onOpenChange={setAsignarDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <Truck className="w-6 h-6 text-torta-brown" />
              Asignar Inventario a Vendedor
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAsignar} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Vendedor</Label>
              <Select
                value={asignarForm.vendedor_id}
                onValueChange={(value) => setAsignarForm({ ...asignarForm, vendedor_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccione vendedor" />
                </SelectTrigger>
                <SelectContent>
                  {vendedores.map((v) => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.nombre} - {v.zona || "Sin zona"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Producto</Label>
              <Select
                value={asignarForm.producto_id}
                onValueChange={(value) => setAsignarForm({ ...asignarForm, producto_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccione producto" />
                </SelectTrigger>
                <SelectContent>
                  {productos.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.nombre} (Stock: {p.stock_central || 0})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Cantidad a asignar</Label>
              <Input
                type="number"
                min="1"
                required
                value={asignarForm.cantidad}
                onChange={(e) => setAsignarForm({ ...asignarForm, cantidad: e.target.value })}
                placeholder="Ingrese cantidad"
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setAsignarDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-torta-brown hover:bg-torta-brown/90">
                Asignar Inventario
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Movimientos Dialog */}
      <Dialog open={movimientosDialogOpen} onOpenChange={setMovimientosDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <History className="w-6 h-6 text-torta-brown" />
              Historial de Movimientos - {selectedProduct?.nombre}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-4">
            {movimientos.length === 0 ? (
              <p className="text-torta-muted text-center py-8">Sin movimientos registrados</p>
            ) : (
              movimientos.map((mov) => (
                <div
                  key={mov.id}
                  className={`p-4 rounded-xl border ${
                    mov.tipo === "entrada"
                      ? "bg-green-50 border-green-200"
                      : mov.tipo === "salida"
                      ? "bg-red-50 border-red-200"
                      : "bg-blue-50 border-blue-200"
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span
                      className={`px-2 py-1 rounded text-xs font-medium ${
                        mov.tipo === "entrada"
                          ? "bg-green-200 text-green-800"
                          : mov.tipo === "salida"
                          ? "bg-red-200 text-red-800"
                          : "bg-blue-200 text-blue-800"
                      }`}
                    >
                      {mov.tipo.toUpperCase()}
                    </span>
                    <span className="text-xs text-gray-500">
                      {new Date(mov.fecha).toLocaleString("es-CO")}
                    </span>
                  </div>
                  <p className="font-medium">
                    {mov.tipo === "entrada" ? "+" : "-"}{mov.cantidad} unidades
                  </p>
                  <p className="text-sm text-gray-600">Por: {mov.usuario_nombre}</p>
                  {mov.notas && (
                    <p className="text-sm text-gray-500 mt-1">Nota: {mov.notas}</p>
                  )}
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
